import { type AttentionUser, isFullySetUp } from "../../../utils/attention";

/** A duplicate-e-mail account. Named for the role it plays in the merge preview. */
export type MergeCandidate = AttentionUser;

export type MergeStep = {
  profileUuid: string;
  profileName: string;
  fromUserUuid: string;
};

export type MergePlan = {
  primary: MergeCandidate;
  /** Accounts that give up their profiles and are deleted afterwards. */
  secondaries: MergeCandidate[];
  /** Profile re-parenting, one mutation each, in execution order. */
  steps: MergeStep[];
};

const time = (value?: string | null): number => {
  const parsed = value ? Date.parse(value) : Number.NaN;
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Rank by how much the account is actually used, so the merge never discards the
 * live one: a usable login beats an unusable one, then recency of login, then a
 * registered second factor, then the number of profiles. uuid last, so the
 * suggestion is stable across renders.
 */
function rank(u: MergeCandidate): [number, number, number, number] {
  return [
    isFullySetUp(u) ? 1 : 0,
    time(u.lastLogin),
    (u.twoFactorMethods?.length ?? 0) > 0 ? 1 : 0,
    u.profiles?.length ?? 0,
  ];
}

/** The account the merge should keep, or null when there is nothing to choose from. */
export function suggestPrimary(users: readonly MergeCandidate[]): string | null {
  const sorted = [...users].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    for (let i = 0; i < ra.length; i++) {
      if (ra[i] !== rb[i]) {
        return (rb[i] ?? 0) - (ra[i] ?? 0);
      }
    }
    return a.uuid.localeCompare(b.uuid);
  });
  return sorted[0]?.uuid ?? null;
}

/**
 * What the merge will do, given the chosen primary. Pure — the modal renders this
 * as the preview and then executes exactly these steps.
 */
export function mergePlan(users: readonly MergeCandidate[], primaryUuid: string): MergePlan | null {
  const primary = users.find((u) => u.uuid === primaryUuid);
  if (!primary) {
    return null;
  }
  const secondaries = users.filter((u) => u.uuid !== primaryUuid);
  const steps = secondaries.flatMap((u) =>
    (u.profiles ?? []).map((p) => ({
      profileUuid: p.uuid,
      profileName: p.listName || p.organization?.name || p.uuid,
      fromUserUuid: u.uuid,
    })),
  );
  return { primary, secondaries, steps };
}
